import type { ChatbotWithIdentity } from '../../../types/chatbot';
import { ChatbotAiConfigurationCard } from './ChatbotAiConfigurationCard';
import { ChatbotAppearanceCard } from './ChatbotAppearanceCard';
import { ChatbotCorsOriginsCard } from './ChatbotCorsOriginsCard';
import { ChatbotEmbedSnippetCard } from './ChatbotEmbedSnippetCard';
import { ChatbotPreviewToggleCard } from './ChatbotPreviewToggleCard';
import { ChatbotPublicAccessCard } from './ChatbotPublicAccessCard';
import { useChatbotConfigurationTab } from './useChatbotConfigurationTab';
import './ChatbotConfigurationTab.css';

type ChatbotConfigurationTabProps = {
  chatbot: ChatbotWithIdentity;
  showPreview: boolean;
  onShowPreviewChange: (value: boolean) => void;
};

export function ChatbotConfigurationTab({
  chatbot,
  showPreview,
  onShowPreviewChange,
}: ChatbotConfigurationTabProps) {
  const config = useChatbotConfigurationTab({
    chatbot,
    showPreview,
    onShowPreviewChange,
  });

  return (
    <section className="chatbot-settings">
      <ChatbotPreviewToggleCard
        draftShowPreview={config.draftShowPreview}
        isDirty={config.isPreviewDirty}
        discardLabel={config.discardLabel}
        saveLabel={config.saveLabel}
        onDraftChange={config.setDraftShowPreview}
        onDiscard={config.handleDiscardPreview}
        onSave={config.handleSavePreview}
      />

      <ChatbotPublicAccessCard
        isPublic={config.isPublic}
        isDirty={config.isPublicDirty}
        isSaving={config.isSaving}
        discardLabel={config.discardLabel}
        saveLabel={config.saveLabel}
        onPublicChange={config.setIsPublic}
        onDiscard={config.handleDiscardPublic}
        onSave={config.savePublic}
      />

      <ChatbotAppearanceCard
        logoDisplaySrc={config.logoDisplaySrc}
        isLogoSaving={config.isLogoSaving}
        logoError={config.logoError}
        displayName={config.displayName}
        primaryColor={config.primaryColor}
        welcomeMessage={config.welcomeMessage}
        placement={config.placement}
        isDirty={config.isAppearanceCardDirty}
        isSaving={config.isSaving}
        isSaveDisabled={config.isAppearanceSaveDisabled}
        discardLabel={config.discardLabel}
        saveLabel={config.saveLabel}
        onLogoChange={config.handleLogoChange}
        onRemoveLogo={config.handleRemoveLogo}
        onDisplayNameChange={config.setDisplayName}
        onPrimaryColorChange={config.setPrimaryColor}
        onWelcomeMessageChange={config.setWelcomeMessage}
        onPlacementChange={config.setPlacement}
        onDiscard={config.handleDiscardAppearance}
        onSave={config.handleSaveAppearance}
      />

      <ChatbotAiConfigurationCard
        systemPrompt={config.systemPrompt}
        maxResults={config.maxResults}
        minScore={config.minScore}
        isDirty={config.isAiConfigurationDirty}
        isSaving={config.isSaving}
        discardLabel={config.discardLabel}
        saveLabel={config.saveLabel}
        onSystemPromptChange={config.setSystemPrompt}
        onMaxResultsChange={config.setMaxResults}
        onMinScoreChange={config.setMinScore}
        onDiscard={config.handleDiscardAiConfiguration}
        onSave={config.saveAiConfiguration}
      />

      <ChatbotCorsOriginsCard
        origins={config.origins}
        newOrigin={config.newOrigin}
        canAddOrigin={config.canAddOrigin}
        isDirty={config.isCorsDirty}
        isSaving={config.isSaving}
        discardLabel={config.discardLabel}
        saveLabel={config.saveLabel}
        onNewOriginChange={config.setNewOrigin}
        onAddOrigin={config.handleAddOrigin}
        onRemoveOrigin={config.handleRemoveOrigin}
        onDiscard={config.handleDiscardCors}
        onSave={config.saveCors}
      />

      <ChatbotEmbedSnippetCard chatbotId={config.chatbotId} />
    </section>
  );
}
